import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import AgingBalanceResumeRow from './AgingBalanceResumeRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ agingBalanceResumeList }) =>
  agingBalanceResumeList.map((item, index) => (
    <AgingBalanceResumeRow key={item.id || index} {...item} />
  ));

Tbody.propTypes = {
  agingBalanceResumeList: PropTypes.array.isRequired
};

const AgingBalanceResumeTable = ({
  className,
  agingBalanceResumeList,
  count,
  page,
  handleChangePage,
  loading,
  ...rest
}) => {
  const { t } = useTranslation();

  const theads = useMemo(
    () => () => (
      <TableRow>
        <TableCell variant="head" key="date" sx={{ width: '12%' }} align="left">
          {t('COMMON.DATE')}
        </TableCell>
        <TableCell
          variant="head"
          key="totalBalance"
          align="left"
          sx={{ width: '13%' }}
        >
          {t('AGING_BALANCE.TOTAL_BALANCE')}
        </TableCell>
        <TableCell
          variant="head"
          key="current"
          align="left"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.UP_TO_DATE')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_1_30"
          align="left"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_1_30')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_31_60"
          align="left"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_31_60')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_61_90"
          align="left"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_61_90')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_91_120"
          align="left"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_91_120')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_over_120"
          align="left"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_OVER_120')}
        </TableCell>
      </TableRow>
    ),
    [t]
  );

  const tbody = useMemo(
    () => () => <Tbody agingBalanceResumeList={agingBalanceResumeList} />,
    [agingBalanceResumeList]
  );

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, count, handleChangePage, t]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

AgingBalanceResumeTable.propTypes = {
  className: PropTypes.string,
  agingBalanceResumeList: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool
};

AgingBalanceResumeTable.defaultProps = {
  count: 0
};

export default AgingBalanceResumeTable;
